import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { endOfDay } from 'date-fns';
import { isNaN, isNull, isUndefined } from 'lodash';
import DateFnsUtils from '@date-io/date-fns';
import FieldLabel from '../ValidationFormElements/FieldLabel';
import useDeviceResize from '../../hooks/useDeviceResize';
import { getActiveLocale } from '../../helpers/langHelpers';

import useStyles from './makeStyles';

const DatePicker = ({
  date,
  onDateChange,
  minDate,
  maxDate,
  disabled,
  labelKey,
  required,
  name,
  invalidDateMessage,
  autoComplete,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isDeviceLgSize = useDeviceResize('lg');

  const commonProps = {
    ...(!isUndefined(minDate) && !isNull(minDate) && { minDate }),
    ...(maxDate && { maxDate: endOfDay(maxDate) }),
    ...(isDeviceLgSize && { okLabel: '' }),
    ...(isDeviceLgSize && { cancelLabel: '' }),
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getActiveLocale()}>
      <KeyboardDatePicker
        {...commonProps}
        name={name}
        id={name}
        className={classes.root}
        inputProps={{
          placeholder: t('datePlaceholder'),
          autoComplete,
        }}
        variant={
          isDeviceLgSize ? 'dialog' : 'inline'
        }
        inputVariant="outlined"
        fullWidth
        size="small"
        label={<FieldLabel labelKey={labelKey} required={required} />}
        format="dd/MM/yyyy"
        value={date}
        minDateMessage=""
        maxDateMessage=""
        disabled={disabled}
        onAccept={(newDate) => {
          if (newDate instanceof Date && !isNaN(newDate)) {
            onDateChange(newDate);
          }
        }}
        onChange={(newDate) => {
          // eslint-disable-next-line no-restricted-globals
          if (newDate instanceof Date && !isNaN(newDate)) {
            onDateChange(newDate);
          }
          if (isNull((newDate))) {
            onDateChange(null);
          }
        }}
        autoOk
        error={false}
        invalidDateMessage={invalidDateMessage}
      />
    </MuiPickersUtilsProvider>
  );
};

DatePicker.propTypes = {
  onDateChange: PropTypes.func,
  disabled: PropTypes.bool,
  labelKey: PropTypes.string,
  name: PropTypes.string.isRequired,
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  invalidDateMessage: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  required: PropTypes.bool,
  autoComplete: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
};

DatePicker.defaultProps = {
  date: null,
  onDateChange: () => { },
  minDate: undefined,
  maxDate: undefined,
  disabled: false,
  labelKey: '',
  required: false,
  invalidDateMessage: '',
  autoComplete: 'off',
};

export default DatePicker;
